import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { getHideFields } from '../../lib/editFieldsConfig';
import { isPackagingEnabled } from '../../lib/productPackaging';
import { printViaIframe } from '../../lib/printHelper';
import { sortRetentionPeriodsForDisplay } from '../../lib/retentionPeriodDisplay';

import FormFields from '../../components/form/fields';
import FormFieldsCompact from '../../components/form/fieldsCompact';
import Input from '../../components/form/input';
import DyrejournalSaleSearch from '../../components/dyrejournal/search';
import DyrejournalJournalListMini from '../../components/dyrejournal/listJournalMini';
import QrCode from '../../components/gui/qrcode';
import Versions from '../../components/versions';
import StlSnapshotter from '../product/stlSnapshotter';

const PACKAGING_FIELDS = ['packageSize', 'packageUnit', 'baseUnit', 'packageInPrice'];

@withText(props => ({
    orderNumber: <Text id='input.orderNumber-help'>orderNumber</Text>,
    name: <Text id='input.name-help'>name</Text>,
    description: <Text id='input.description-help'>description</Text>,
    installation: <Text id='input.installation-help'>installation</Text>,
    url: <Text id='input.url-help'>url</Text>,
    journalTitle: <Text id='dyrejournal.journal.search-title'>Search for journals</Text>,
    drugTitle: <Text id='dyrejournal.drug.search-title'>Search for drugs</Text>,
}))
@observer
class NewProduct extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            sections: fields.get('product'),
            linkCopied: false,
        };
    }

    createProduct = async () => {
        const { callback = () => {} } = this.props;
        const { productStore, appState } = this.props.stores;
        const { currentCustomer = {}, inputProps = {} } = appState;
        const { showFrontpageShop } = inputProps;
        // if (showFrontpageShop) {
        //     productStore.updateObjectKeyValue('newProduct', 'customer', currentCustomer?.id);
        // }

        const { newProduct } = productStore;
        const response = await productStore.insert(newProduct);
        const product = response?.data?.product;
        productStore.updateKeyValue('newProduct', {});
        if (response.status === 201) {
            callback(product);
        }
    }

    loadAll = async () => {
        const { id, isNew } = this.props;
        const { productStore } = this.props.stores;
        if (!isNew) {
            // await productStore.load(id, null, { addData: ['allProducts'] });
            await productStore.load(id, null, { offset: 0 });
            const { product = {} } = productStore;
            this.loadJournals(product.journal);
            this.loadDrugs(product.drug);
        }
    }

    loadJournals = async id => {
        const { productStore, journalStore } = this.props.stores;
        if (id) {
            const query = {
                id,
            };
            const response = await journalStore.load({
                query,
                skipUpdate: true,
            });
            productStore.updateKeyValue('productJournals', response);
        } else {
            productStore.updateKeyValue('productJournals', []);
        }
    }

    loadDrugs = async id => {
        const { productStore, drugStore } = this.props.stores;
        if (id) {
            const query = {
                id,
            };
            const response = await drugStore.load({
                query,
                skipUpdate: true,
            });
            productStore.updateKeyValue('productDrugs', response);
        } else {
            productStore.updateKeyValue('productDrugs', []);
        }
    }

    displayRetentionPeriods = retentionPeriods => {
        if (!retentionPeriods) {
            return '';
        }
        // Same fixed display order as the journal medicine table (TICKET-01759).
        return sortRetentionPeriodsForDisplay(retentionPeriods).map(({ period: retention }) => {
            return(<>
                <div class='d-flex flex-row'>
                    <div class='me-1 font-weight-bold'>{retention.type}</div>
                    <div class='d-flex flex-row'>
                        {retention.dyreslag.map((dyreslag, idx) => {
                            return(<>
                                <div class='me-1 text-muted'>{dyreslag.dyreslagkode}</div>
                                <div class='me-1'>{dyreslag['antall-dager']}</div>
                            </>);
                        })}
                    </div>
                </div>
            </>);
        });
    }

    displayUsageReason = usageReason => {
        switch (usageReason) {
            case '1':
                return '1: Andre';
            case '2':
                return '2: Forebyggende';
            case '3':
                return '3: Sykdom';
            default:
                return '';
        }
    }

    displayThreatmentBy = threatmentBy => {
        switch (threatmentBy) {
            case '1':
                return '1: Dyreeier/medhjelper';
            case '2':
                return '2: Dyrehelsepersonell';
            default:
                return '';
        }
    }

    searchJournalCallback = async (item) => {
        const { productStore } = this.props.stores;
        const { product } = productStore;
        // console.log('searchJournalCallback', item);
        if (item?.id) {
            console.log('updating journal', item.id, product.id);
            productStore.saveField(product.id, 'journal', item.id, true);
            this.loadJournals(item.id);
        }
        // saleStore.updateKeyValue('isProductionAnimals', isProductionAnimals);
    }

    searchDrugCallback = async (item) => {
        const { productStore } = this.props.stores;
        const { product } = productStore;
        if (item?.id) {
            console.log('updating drug', item.id, product.id);
            productStore.saveField(product.id, 'drug', item.id, true);
            this.loadDrugs(item.id);
        }
    }

    removeJournalRelation = async () => {
        const { productStore } = this.props.stores;
        const { product } = productStore;
        if (product?.id) {
            await productStore.saveField(product.id, 'journal', 0, true);
            productStore.updateKeyValue('productJournals', []);
        }
    }

    removeDrugRelation = async () => {
        const { productStore } = this.props.stores;
        const { product } = productStore;
        if (product?.id) {
            console.log('removing drug relation from product', product.id);
            await productStore.saveField(product.id, 'drug', 0, true);
            productStore.updateKeyValue('productDrugs', []);
        }
    }

    copyBuyLink = async () => {
        const { productStore } = this.props.stores;
        const { product } = productStore;
        const buyUrl = `${window.location.origin}${window.location.pathname}#/buy/${product.id}`;
        try {
            await navigator.clipboard.writeText(buyUrl);
            this.setState({ linkCopied: true });
            setTimeout(() => this.setState({ linkCopied: false }), 2000);
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    }

    printQrCode = () => {
        const { productStore } = this.props.stores;
        const { product } = productStore;
        const buyUrl = `${window.location.origin}${window.location.pathname}#/buy/${product.id}`;

        // Get the rendered QR code SVG from the DOM
        const qrContainer = this.qrRef;
        const svgEl = qrContainer ? (qrContainer.querySelector('svg') || qrContainer.querySelector('canvas')) : null;
        const qrHtml = svgEl ? svgEl.outerHTML : '';

        // Build print content and print via iframe
        const tempDiv = document.createElement('div');
        tempDiv.className = 'print-section';
        tempDiv.innerHTML = `
            <div style="text-align: center; padding: 10mm;">
                <h2 style="margin-bottom: 4px;">${product.name || 'Product'}</h2>
                <div style="display: flex; justify-content: center; margin: 10px 0;">${qrHtml}</div>
                <div style="color: #666; font-size: 11px; word-break: break-all; margin-top: 8px;">${buyUrl}</div>
            </div>
        `;
        const htmlContent = tempDiv.outerHTML;
        printViaIframe(htmlContent);
    }

    openFieldsSettings = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        appState.openDrawer('editFieldsSettings', {
            entityType: 'product',
            height: drawerHeightMediumLarge,
        }, drawerLevel + 1);
    }

    componentDidMount() {
        this.loadAll();
    }

    render() {
        const { journalTitle, drugTitle } = this.props;
        const { week, isNew, hideAdmin, drawerLevel } = this.props;
        const { sections } = this.state;
        const { userStore, productStore, journalStore, drugStore } = this.props.stores;
        const { user, isAdmin, isCustomerAdmin, customer = {} } = userStore;
        const configuredHideFields = getHideFields(customer, 'product');
        // Packaging fields are behind featureFlags.productPackaging. Build a new array:
        // never push into the array returned by getHideFields.
        const hideFields = isPackagingEnabled(customer)
            ? configuredHideFields
            : [...configuredHideFields, ...PACKAGING_FIELDS.map(name => ({ name }))];
        const { newProduct, product, productJournals, productDrugs } = productStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        if (!isCustomerAdmin) {
            return (<>
                <div class='w-100 d-flex justify-content-center'>
                    <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                        <div class='w-100 d-flex flex-column justify-content-start'>
                            <div class='font-weight-lighter px-3 box-header mt-0'>
                                <Text id='product.noAccess'>You do not have access to this page.</Text>
                            </div>
                        </div>
                    </div>
                </div>
            </>)
        }

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    {/* <div class='w-100 d-flex flex-column justify-content-start'>
                        <xmp>{JSON.stringify(user.settings, null, 2)}</xmp>
                    </div> */}
                    {/* <xmp>{JSON.stringify(newCustomer, null, 4)}</xmp> */}
                    <div class='font-weight-lighter px-3 box-header mt-0 d-flex flex-row justify-content-between align-items-center'>
                        <span>
                            {isNew ? <Text id='product.new'>New product</Text> : <Text id='product.edit'>Edit product</Text>}
                        </span>
                        {/* me-5 keeps the cog clear of the drawer close X when the drawer is no wider than the content column. */}
                        {isCustomerAdmin && <button type='button' class='btn btn-sm btn-outline-secondary rounded-circle me-5'
                            onClick={this.openFieldsSettings}>
                            <i class='fa-solid fa-gear' />
                        </button>}
                    </div>
                    <div class='bg-light d-flex flex-column justify-content-start pt-3'>
                        <FormFieldsCompact
                            stores={this.props.stores}
                            sections={sections}
                            holdingStore={productStore}
                            objectName={isNew ? 'newProduct' : 'product'}
                            isNew={isNew}
                            drawerLevel={drawerLevel}
                            hideAdmin={hideAdmin}
                            hideFields={hideFields}
                        />
                    </div>

                    {!isNew && product?.id > 0 && (
                        <StlSnapshotter
                            stores={this.props.stores}
                            object={product}
                            objectName='product'
                        />
                    )}

                    <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-4'>
                        <Text id='product.related-journal'>Related journal template</Text>
                    </div>
                    <div class={`d-flex flex-column mx-3 px-2 py-2 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        {product?.id ? <>
                            <DyrejournalSaleSearch
                                stores={this.props.stores}
                                key={'journal'}
                                title={journalTitle}
                                holdingStore={journalStore}
                                searchResultList={'searchResults'}
                                searchResultsLine={(e, idx, selectedIndex) => {
                                    return(<>
                                        <div class={`d-flex flex-row justify-content-between px-3 py-1 ${idx === selectedIndex ? 'selected' : ''}`}>
                                            <div>{e?.title} {e?.date}</div>
                                        </div>
                                    </>);
                                }}
                                callback={this.searchJournalCallback}
                                skipSaleUpdate={true}
                                ref={c => this.journalSearchRef = c}
                                // rightButton={hasVisitor && newSaleTItle}
                                // rightButtonClassNames='btn-success'
                                // rightButtonClick={this.onClickNewSale}
                            />
                            <DyrejournalJournalListMini
                                classNames='mx-1'
                                stores={this.props.stores}
                                journals={productJournals}
                                drawerLevel={drawerLevel}
                                // saleIsLocked={saleIsLocked}
                                // animals={[e]}
                                collapse={true}
                                // saveSale={saveSale}
                                viewOnly={true}
                                noSendTo={true}
                            />
                            {productJournals && productJournals.length > 0 && (
                                <div class='d-flex justify-content-end mt-1'>
                                    <button
                                        class='btn btn-sm btn-outline-danger'
                                        onClick={this.removeJournalRelation}
                                        title='Remove journal relation'
                                    >
                                        <i class='fa-solid fa-times' /> <Text id='product.remove-journal'>Remove journal</Text>
                                    </button>
                                </div>
                            )}
                        </> : <>
                            <div class='d-flex flex-row justify-content-between px-3 py-1'>
                                <div><Text id='product.no-product-selected'>No product selected</Text></div>
                            </div>
                        </>}
                    </div>

                    <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-4'>
                        <Text id='product.related-drug'>Related drug</Text>
                    </div>
                    <div class={`d-flex flex-column mx-3 px-2 py-2 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        {product?.id ? <>
                            <DyrejournalSaleSearch
                                stores={this.props.stores}
                                key={'drug'}
                                title={drugTitle}
                                holdingStore={drugStore}
                                searchResultList={'searchResults'}
                                searchResultsLine={(e, idx, selectedIndex) => {
                                        return(<>
                                            <div class={`d-flex flex-row justify-content-between px-3 py-1 ${idx > 0 ? 'border-top' : ''} ${idx === selectedIndex ? 'bg-primary-light' : ''}`}>
                                                <div>
                                                    {e.navnFormStyrke || e.navn} ({e.pakningsinfoPakningsstr || e.pakningsinfoMengde} {e.pakningsinfoEnhetPakningV} {e.preparattype})<br />
                                                    <span class='font-weight-lighter text-info'>
                                                        {e.amountPerAnimal}
                                                        {e.unit ? ` ${e.unit}` : ''}
                                                        {e.no ? ` - ${e.no}` : ''}
                                                        {e.usageReason ? ` - ${this.displayUsageReason(e.usageReason)}` : ''}
                                                        {e.threatmentBy ? ` - ${this.displayThreatmentBy(e.threatmentBy)}` : ''}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span class='font-weight-lighter text-muted'>{this.displayRetentionPeriods(e.tilbakeholdsfrister)}</span><br />
                                                    <span class='font-weight-lighter text-info'>{this.displayRetentionPeriods(e.retentionPeriods)}</span>
                                                </div>
                                            </div>
                                        </>);
                                    }}
                                callback={this.searchDrugCallback}
                                skipSaleUpdate={true}
                                ref={c => this.drugSearchRef = c}
                            />
                            {productDrugs && productDrugs.length > 0 && <>
                                <div class='table-responsive mx-1 mt-2'>
                                    <table class='table table-sm table-striped mb-0'>
                                        <thead>
                                            <tr>
                                                <th><Text id='drug.name'>Name</Text></th>
                                                <th><Text id='drug.atc'>ATC</Text></th>
                                                <th><Text id='drug.form'>Form</Text></th>
                                                <th><Text id='drug.package'>Package</Text></th>
                                                <th><Text id='drug.unit'>Unit</Text></th>
                                                <th><Text id='drug.preparation-type'>Type</Text></th>
                                                <th><Text id='drug.retention-periods'>Retention Periods</Text></th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {productDrugs.map((drug, idx) => {
                                                return (<tr>
                                                    <td>{drug.navnFormStyrke || drug.navn}</td>
                                                    <td class='font-weight-lighter'>{drug.atc}</td>
                                                    <td class='font-weight-lighter'>{drug.form}</td>
                                                    <td class='font-weight-lighter'>
                                                        {drug.pakningsinfoMengde && drug.pakningsinfoEnhetPakningV ?
                                                            `${drug.pakningsinfoPakningsstr || drug.pakningsinfoMengde} ${drug.pakningsinfoEnhetPakningV}` : ''}
                                                    </td>
                                                    <td class='font-weight-lighter'>{drug.unit}</td>
                                                    <td class='font-weight-lighter'>{drug.preparattype}</td>
                                                    <td class='font-weight-lighter'>
                                                        {this.displayRetentionPeriods(drug.tilbakeholdsfrister || drug.retentionPeriods)}
                                                    </td>
                                                    <td>
                                                        <button
                                                            class='btn btn-sm btn-outline-danger'
                                                            onClick={this.removeDrugRelation}
                                                            title='Remove drug relation'
                                                        >
                                                            <i class='fa-solid fa-times' />
                                                        </button>
                                                    </td>
                                                </tr>);
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>}
                        </> : <>
                            <div class='d-flex flex-row justify-content-between px-3 py-1'>
                                <div><Text id='product.no-product-selected'>No product selected</Text></div>
                            </div>
                        </>}
                    </div>


                    {!isNew && product?.id && <>
                        <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-4'>
                            <Text id='product.quick-buy-qr'>Quick Buy QR Code</Text>
                        </div>
                        <div class={`d-flex flex-column mx-3 px-2 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class='d-flex flex-column align-items-center'>
                                <div ref={c => this.qrRef = c}>
                                    <QrCode
                                        value={`${window.location.origin}${window.location.pathname}#/buy/${product.id}`}
                                        size={200}
                                        level='M'
                                        margin={1}
                                    />
                                </div>
                                <small class='text-body-secondary mt-2 text-center' style='word-break: break-all;'>
                                    {`${window.location.origin}${window.location.pathname}#/buy/${product.id}`}
                                </small>
                                <div class='d-flex flex-row mt-3'>
                                    <button
                                        type='button'
                                        class={`btn btn-sm ${this.state.linkCopied ? 'btn-success' : 'btn-outline-primary'} rounded-pill me-2`}
                                        onClick={this.copyBuyLink}
                                    >
                                        <i class={`fa-solid ${this.state.linkCopied ? 'fa-check' : 'fa-copy'} me-1`} />
                                        {this.state.linkCopied ? <Text id='product.link-copied'>Copied!</Text> : <Text id='product.copy-link'>Copy link</Text>}
                                    </button>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-secondary rounded-pill'
                                        onClick={this.printQrCode}
                                    >
                                        <i class='fa-solid fa-print me-1' />
                                        <Text id='product.print'>Print</Text>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>}

                    {!isNew && product?.id && (
                        <div class='mt-4 px-3'>
                            <Versions
                                stores={this.props.stores}
                                type='product'
                                id={product.id}
                                key={`product-versions-${product.id}`}
                            />
                        </div>
                    )}

                    {isNew && <div class='w-100 d-flex flex-column justify-content-start mt-3 px-4 py-3 bg-light fixed-bottom' style={`bottom: 0; left: 0;`}>
                        <button type='button' class='btn btn-primary rounded-pill' onClick={this.createProduct}>
                            {isNew ? <Text id='product.create'>Create product</Text> : <Text id='product.save'>Save product</Text>}
                        </button>
                    </div>}
                </div>
            </div>
        </>);
    }
}

export default NewProduct;
